// Pure helper functions for the CSV upload post-registration lifecycle
// (status polling, status-message formatting, insert_batch_id formatting, and
// POI -> place mapping).
//
// These are intentionally free of any Vue/component dependency (and any
// third-party library) so that fast-check property tests can import and
// exercise them directly. They mirror the extraction pattern used by
// placeSelectionCsv.js / placeSelectionBuilderHelpers.js in this same folder.
//
// Feature: csv-upload-status-and-poi-display (Task 1)

/**
 * The Poll_Interval: elapsed time (ms) the Front-End waits between Poll_Cycles,
 * measured from completion of the previous cycle (Requirement 3.1).
 * @type {number}
 */
export const POLL_INTERVAL_MS = 5000;

/**
 * The Max_Poll_Attempts: maximum number of Poll_Cycles performed before the job
 * is treated as timed out (Requirement 3.2). 60 cycles at 5s each gives a
 * ~5-minute ceiling.
 * @type {number}
 */
export const MAX_POLL_ATTEMPTS = 60;

/**
 * Prefix applied to a Job_Id to form the Insert_Batch_Id search filter value,
 * i.e. `"aui:" + Job_Id` (Requirement 6.2).
 * @type {string}
 */
export const INSERT_BATCH_PREFIX = "aui:";

/**
 * The `placesType` value sent when fetching a completed CSV job's POIs
 * (Requirement 6.2).
 * @type {string[]}
 */
export const POI_PLACES_TYPE = ["Polygons", "POI"];

/**
 * Recognized terminal Processing_Status values. `"completed"` is the success
 * terminal status; failure statuses are recognized separately via
 * {@link isFailureStatus}.
 * @type {{ COMPLETED: string }}
 */
export const TERMINAL_STATUSES = { COMPLETED: "completed" };

/**
 * One element of the Places API library-list payload.
 *
 * @typedef {Object} LibraryEntry
 * @property {string} id     The Job_Id (matched against the captured registration id).
 * @property {string} name   Internal upload name.
 * @property {string} status Processing_Status: `"processing"`, `"completed"`, or a failure status.
 * @property {Stats}  stats  The success/failure/total counts for the job.
 */

/**
 * The counts describing a CSV job's outcome.
 *
 * @typedef {Object} Stats
 * @property {number} total   Total rows considered.
 * @property {number} success Rows that succeeded.
 * @property {number} failure Rows that failed.
 */

/**
 * A raw POI record returned by the POI_Search_Proxy `records` array.
 *
 * @typedef {Object} Poi
 * @property {string} [near_poi_id]   Preferred identifier.
 * @property {string} [placematrix_id] Secondary identifier.
 * @property {string} [id]            Fallback identifier.
 * @property {string} [name]          Display name.
 * @property {string} [unique_name]   Fallback display name.
 * @property {Object} [geometry]      GeoJSON geometry (usually absent on search-pois records).
 * @property {string} [country]       Country.
 * @property {string} [address]       Address.
 * @property {string} [address_full]  Fallback address.
 * @property {string} [category]      Category.
 * @property {string} [cat1]          Primary category (search-pois field).
 * @property {string} [cat2]          Sub-category (search-pois field), preferred for display.
 * @property {string} [brand]         Brand.
 * @property {string} [poi_list_name] Brand/list name (search-pois field).
 * @property {string[]} [labels]      Labels/tags.
 * @property {string[]} [tags]        Alternate labels source.
 */

/**
 * A normalized place object suitable for the placeSelection store's
 * `addSelectedPlaces` mutation, routed into the Custom_Group.
 *
 * @typedef {Object} Place
 * @property {string}  _id           Stable dedupe key (string form of the chosen id).
 * @property {string}  _source       Source indicator; always `'library'` for CSV POIs.
 * @property {boolean} custom        Custom-place classification marker; always `true`.
 * @property {string|null} near_poi_id   Passthrough identifier.
 * @property {string|null} placematrix_id Passthrough identifier.
 * @property {string}  name          Preserved place name.
 * @property {Object}  geometry      Preserved, unaltered geometry.
 * @property {string|null} country   Passthrough country.
 * @property {string|null} unique_name Passthrough unique name.
 * @property {string|null} address   Passthrough address.
 * @property {string|null} category  Passthrough category.
 * @property {string|null} brand     Passthrough brand.
 * @property {string[]} labels       Passthrough labels/tags.
 */

/**
 * Select the Library_Entry whose `id` equals `jobId`.
 *
 * Returns the first matching entry, or `null` when `entries` is not an array or
 * no entry matches. A `null` result drives the "job absent → keep processing"
 * branch of the poller (Requirement 2.3, 2.7).
 *
 * @param {LibraryEntry[]} entries - The library-list payload array.
 * @param {string} jobId - The Job_Id to match.
 * @returns {LibraryEntry|null} The matching entry, or null.
 */
export function selectEntryById(entries, jobId) {
  if (!Array.isArray(entries)) return null;
  const match = entries.find((entry) => entry && entry.id === jobId);
  return match || null;
}

/**
 * Determine whether a Processing_Status is a recognized failure status.
 *
 * Case-insensitive; `true` for `'failed'`, `'failure'`, and `'error'`.
 *
 * @param {string} status - The Processing_Status.
 * @returns {boolean} True for a recognized failure status.
 */
export function isFailureStatus(status) {
  const s = String(status || "").toLowerCase();
  return s === "failed" || s === "failure" || s === "error";
}

/**
 * Determine whether a Processing_Status is terminal.
 *
 * Case-insensitive; `true` for `'completed'` and any recognized failure status
 * (via {@link isFailureStatus}); `false` for `'processing'` and for any
 * absent/unknown status (treated as still-processing so the poller keeps going
 * until timeout — Requirement 2.5, 2.6, 2.7).
 *
 * @param {string} status - The Processing_Status.
 * @returns {boolean} True when the status will not change further.
 */
export function isTerminalStatus(status) {
  const s = String(status || "").toLowerCase();
  return s === "completed" || isFailureStatus(s);
}

/**
 * Whether `v` is a non-negative integer, i.e. `Number.isInteger(v) && v >= 0`.
 * Used to gate each count so that absent, negative, non-integer, `NaN`, or
 * non-numeric values are never rendered into the Status_Message
 * (Requirement 4.4).
 *
 * @param {*} v - The candidate count.
 * @returns {boolean} True when `v` is a non-negative integer.
 */
function isNonNegativeInteger(v) {
  return Number.isInteger(v) && v >= 0;
}

/**
 * Build the Status_Message describing progress and/or the success/failure
 * summary of a CSV job.
 *
 * Never renders `undefined` or `NaN` and omits any count that is absent or not
 * a non-negative integer (Requirements 4.1–4.4):
 *   - `'processing'` → a processing message with no counts.
 *   - `'completed'`  → success + failure out of total, each rendered only when
 *     a non-negative integer.
 *   - failure status → a failed message including failure out of total when
 *     each is a non-negative integer.
 *
 * @param {string} status - The Processing_Status.
 * @param {Stats} [stats] - The job's counts.
 * @returns {string} The Status_Message.
 */
export function formatStatusMessage(status, stats) {
  const s = String(status || "").toLowerCase();
  const src = stats && typeof stats === "object" ? stats : {};
  const total = isNonNegativeInteger(src.total) ? src.total : null;
  const success = isNonNegativeInteger(src.success) ? src.success : null;
  const failure = isNonNegativeInteger(src.failure) ? src.failure : null;

  if (s === "completed") {
    // e.g. "Upload complete: 42 succeeded, 3 failed out of 45."
    const parts = [];
    if (success !== null) parts.push(`${success} succeeded`);
    if (failure !== null) parts.push(`${failure} failed`);
    let message = "Upload complete";
    if (parts.length) {
      message += `: ${parts.join(", ")}`;
      if (total !== null) message += ` out of ${total}`;
    }
    return `${message}.`;
  }

  if (isFailureStatus(s)) {
    // e.g. "Upload failed: 3 of 45 rows could not be processed."
    if (failure !== null && total !== null) {
      return `Upload failed: ${failure} of ${total} rows could not be processed.`;
    }
    if (failure !== null) {
      return `Upload failed: ${failure} rows could not be processed.`;
    }
    return "Upload failed.";
  }

  // 'processing', absent, and any unknown status -> processing message, no counts.
  return "Processing your uploaded places\u2026";
}

/**
 * Build the count-free Status_Message shown when Max_Poll_Attempts is reached
 * without a Terminal_Status (Requirement 3.3).
 *
 * @returns {string} The timeout Status_Message.
 */
export function formatTimeoutMessage() {
  return "Still processing your uploaded places. This is taking longer than expected \u2014 please check back later.";
}

/**
 * Build the count-free error Status_Message for a failed poll cycle or POI
 * fetch (Requirements 8.2, 8.3).
 *
 * @param {'status'|'pois'} kind - `'status'` when the status could not be
 *   retrieved; `'pois'` when the places could not be loaded.
 * @returns {string} The error Status_Message.
 */
export function formatStatusErrorMessage(kind) {
  if (kind === "pois") {
    return "Your uploaded places could not be loaded. Please try again later.";
  }
  return "Upload status could not be retrieved. Please try again later.";
}

/**
 * Build the Insert_Batch_Id search filter value for a Job_Id, i.e. exactly
 * `` `${INSERT_BATCH_PREFIX}${jobId}` `` = `"aui:" + jobId`, with no trimming or
 * re-casing (Requirement 6.2).
 *
 * @param {string} jobId - The Job_Id.
 * @returns {string} The Insert_Batch_Id.
 */
export function buildInsertBatchId(jobId) {
  return `${INSERT_BATCH_PREFIX}${jobId}`;
}

/**
 * Map a Places API POI record (a `poi_response_list` element from
 * `places/search/pois`) to a normalized {@link Place} suitable for
 * `addSelectedPlaces`, preserving the identifier and setting the Custom-group
 * markers `_source: 'library'` and `custom: true` (Requirements 7.1, 7.2).
 *
 * Returns `null` only when the POI is missing a usable identifier
 * (`near_poi_id` / `placematrix_id` / `id`). Geometry is NOT required: the
 * `places/search/pois` response identifies places by id and does not carry a
 * GeoJSON `geometry` (geometry is resolved downstream — e.g. by the Builder →
 * Job_Request sync via `hasResolvableLocation`, mirroring how `PlacesPage`
 * fetches geometry per `near_poi_id`). Requiring geometry here would drop every
 * search record and leave the Custom group empty. When a record does carry a
 * geometry it is preserved unaltered.
 *
 * Field fallbacks mirror `placesAdapter.normalizePlaceRecord` so a CSV POI reads
 * the same as any other library place: `unique_name`/`name` for the display
 * name, `address`/`address_full`, `cat2`/`cat1` for category, and
 * `poi_list_name`/`brand` for brand.
 *
 * @param {Poi} poi - The raw POI record.
 * @returns {Place|null} The mapped place, or null when it has no usable id.
 */
export function poiToPlace(poi) {
  if (!poi || typeof poi !== "object") return null;
  const id = poi.near_poi_id || poi.placematrix_id || poi.id || null;
  if (!id) return null; // Requirement 7.6 (no usable identifier)
  const place = {
    _id: String(id), // stable id -> store dedupe by _id (Req 7.4)
    _source: "library", // Requirement 7.1, 7.2
    custom: true, // Requirement 7.1, 7.2 (Custom group)
    near_poi_id: poi.near_poi_id || null,
    placematrix_id: poi.placematrix_id || null,
    name: poi.name || poi.unique_name || "Unnamed place", // Requirement 7.5
    country: poi.country || null,
    unique_name: poi.unique_name || null,
    address: poi.address || poi.address_full || null,
    category: poi.cat2 || poi.cat1 || poi.category || null,
    brand: poi.poi_list_name || poi.brand || null,
    labels: Array.isArray(poi.labels)
      ? poi.labels
      : Array.isArray(poi.tags)
        ? poi.tags
        : [],
  };
  // Preserve any geometry the record happens to carry (unaltered); most
  // search-pois records omit it, in which case the place is added without one.
  if (poi.geometry) place.geometry = poi.geometry; // Requirement 7.5
  return place;
}

/**
 * Map a list of raw POI records through {@link poiToPlace} and drop the `null`s
 * (POIs missing a usable identifier), returning the displayable mapped places
 * in order (Requirements 6.6, 7.6).
 *
 * @param {Poi[]} pois - The raw POI records.
 * @returns {Place[]} The displayable, mapped places (possibly empty).
 */
export function filterDisplayablePois(pois) {
  if (!Array.isArray(pois)) return [];
  const places = [];
  for (const poi of pois) {
    const place = poiToPlace(poi);
    if (place !== null) places.push(place);
  }
  return places;
}

/**
 * Pure scheduling predicate: whether another Poll_Cycle may be scheduled given
 * the number of completed attempts. `true` while `attempts < MAX_POLL_ATTEMPTS`
 * (Requirements 3.2, 3.5, 8.1).
 *
 * @param {number} attempts - The number of completed Poll_Cycles.
 * @returns {boolean} True when the poller may schedule another cycle.
 */
export function shouldScheduleNext(attempts) {
  return attempts < MAX_POLL_ATTEMPTS;
}

/**
 * Read the `id` field off a nested path of the CSV registration response
 * without throwing on missing intermediate values.
 *
 * @param {*} obj - The object to read from.
 * @param {string} path - A dot-delimited path (e.g. `"payload.id"`).
 * @returns {*} The value at `path`, or `undefined`.
 */
function readResponsePath(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return acc[key];
  }, obj);
}

/**
 * The candidate response paths that may carry the CSV registration Job_Id, in
 * priority order. The `POST /v2/places/library` response places the Places API
 * library entry `id` at `payload.id`; the defensive fallbacks cover an extra
 * `data` envelope (`data.payload.id`) and a bare top-level `id`.
 *
 * IMPORTANT: this is deliberately separate from `extractSavedPoiId`
 * (placeSelectionSaveHelpers.js), whose probe paths (`near_poi_id`,
 * `data.near_poi_id`, `payload.near_poi_id`, `id`, `data.id`, ...) never reach
 * `payload.id`. Reusing that helper for a library registration returns `null`,
 * which is the root cause of the poller never starting.
 * @type {string[]}
 */
const JOB_ID_PATHS = ["payload.id", "data.payload.id", "id"];

/**
 * Extract the CSV registration Job_Id (the Places API library entry `id`) from
 * a `POST /v2/places/library` response. Probes {@link JOB_ID_PATHS} in order and
 * returns the first non-empty string value (numbers are coerced to strings),
 * or `null` when none is present (Requirement 2.9).
 *
 * The returned value belongs in the modal's dedicated `jobIdsById` map and must
 * NOT be merged into `poiIdsById` (which carries `near_poi_id` values consumed
 * by `markSelectedPlaceSaved`).
 *
 * Defensive on non-object input (returns `null`).
 *
 * @param {*} response - The registration response body.
 * @returns {string|null} The Job_Id, or `null` when absent.
 */
export function extractJobId(response) {
  if (!response || typeof response !== "object") {
    return null;
  }
  for (const path of JOB_ID_PATHS) {
    const value = readResponsePath(response, path);
    if (typeof value === "string" && value.trim() !== "") {
      return value;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
}
